import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";

const StoreContext = createContext(null);

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("moha66_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("moha66_admin_token") || "");

  useEffect(() => {
    localStorage.setItem("moha66_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    api.get("/settings").then(setSettings).catch(() => {});
  }, []);

  const addToCart = useCallback((product, size, color, quantity, customText = "") => {
    setCart((prev) => {
      const key = `${product.id}|${size}|${color || ""}|${customText || ""}`;
      const existing = prev.find((i) => `${i.product_id}|${i.size}|${i.color || ""}|${i.custom_text || ""}` === key);
      if (existing) {
        return prev.map((i) => (
          `${i.product_id}|${i.size}|${i.color || ""}|${i.custom_text || ""}` === key
            ? { ...i, quantity: Math.min(i.quantity + quantity, 20) }
            : i
        ));
      }
      return [...prev, {
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        price: product.price,
        image: (product.images && product.images[0]) || "",
        size,
        color: color || "",
        quantity,
        custom_text: (customText || "").trim(),
      }];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((idx, quantity) => {
    setCart((prev) => prev.map((i, j) => (j === idx ? { ...i, quantity: Math.max(1, Math.min(quantity, 20)) } : i)));
  }, []);

  const removeFromCart = useCallback((idx) => {
    setCart((prev) => prev.filter((_, j) => j !== idx));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const count = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  const shippingFee = useMemo(() => {
    if (!settings || count === 0) return null;
    const sh = settings.shipping || {};
    return subtotal >= (sh.free_threshold || 0) && (sh.free_threshold || 0) > 0 ? 0 : null;
  }, [settings, subtotal, count]);

  const setAdminTokenSafe = useCallback((t) => {
    setAdminToken(t);
    if (t) localStorage.setItem("moha66_admin_token", t);
    else localStorage.removeItem("moha66_admin_token");
  }, []);

  return (
    <StoreContext.Provider value={{
      cart, addToCart, updateQty, removeFromCart, clearCart,
      subtotal, count, drawerOpen, setDrawerOpen,
      settings, adminToken, setAdminToken: setAdminTokenSafe,
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
